import React, { useMemo, useState, useEffect } from 'react';
import { Button, Card } from 'react-bootstrap';
import { ReceptionLoadingBlock, ReceptionEmptyState } from '../ui';
import CompanyService from '../../../services/companyService';
import CompanyListRows from './CompanyListRows';
import BrowseSearchBar from '../browse/BrowseSearchBar';

export default function ReceptionCompaniesBrowse({ toast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await CompanyService.getActiveCompanies();
        if (cancelled) return;
        if (res.success) setRows(Array.isArray(res.data) ? res.data : []);
        else toast('warning', res.message || 'Réponse sociétés inattendue');
      } catch (e) {
        if (!cancelled) toast('danger', e.message || 'Impossible de charger les sociétés');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) =>
      [c.name, c.city, c.phone, c.mail, c.company_code, c.affiliation_name].some((v) =>
        String(v || '').toLowerCase().includes(q)
      )
    );
  }, [rows, search]);

  const isFiltered = Boolean(search.trim());
  const countLabel = loading
    ? 'Chargement…'
    : `${filteredRows.length} société${filteredRows.length > 1 ? 's' : ''}${
        isFiltered && filteredRows.length !== rows.length ? ` sur ${rows.length}` : ''
      }`;

  const listHead = (
    <div className="recv-browse-list__head" aria-hidden="true">
      <span className="recv-browse-list__cell recv-browse-list__cell--lead">Société</span>
      <span className="recv-browse-list__cell recv-browse-list__cell--contact">Contact</span>
    </div>
  );

  return (
    <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-panel-card recv-panel-card--browse recv-panel-card--browse-companies">
      <Card.Header className="recv-browse-panel__header border-bottom-0 pb-0">
        <div className="recv-browse-panel__hero">
          <div className="recv-browse-panel__intro">
            <div className="recv-browse-panel__eyebrow">Annuaire partenaires</div>
            <div className="recv-browse-panel__title">Sociétés actives</div>
            <p className="recv-browse-panel__desc mb-0">
              Consultation en lecture seule — {countLabel}.
            </p>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="recv-browse-panel__body pt-0">
        <BrowseSearchBar
          id="recv-companies-search"
          label="Rechercher une société"
          placeholder="Nom, ville, code, mail, téléphone…"
          value={search}
          onChange={setSearch}
          hint={
            isFiltered
              ? `${filteredRows.length} résultat${filteredRows.length > 1 ? 's' : ''} sur ${rows.length} société${rows.length > 1 ? 's' : ''}.`
              : 'Filtrez par nom, ville ou coordonnées de contact.'
          }
        />

        {loading ? (
          <ReceptionLoadingBlock label="Chargement des sociétés…" />
        ) : filteredRows.length === 0 ? (
          <ReceptionEmptyState
            title={isFiltered ? 'Aucun résultat' : 'Aucune société'}
            description={
              isFiltered
                ? 'Affinez la recherche ou effacez le filtre.'
                : "Aucune société active n'est répertoriée pour le moment."
            }
            action={
              isFiltered ? (
                <Button variant="outline-primary" size="sm" onClick={() => setSearch('')}>
                  Effacer la recherche
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="recv-browse-list-wrap recv-browse-list-wrap--companies border rounded bg-white mt-3">
            <div className="recv-browse-list" role="list" aria-label="Liste des sociétés">
              {listHead}
              <CompanyListRows rows={filteredRows} />
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
